import { useState, useMemo } from "react";
import { QrCode, UserPlus, Bell, MoreHorizontal, ChevronDown, ChevronRight, User, Package, CalendarPlus, MapPin, CalendarDays, Clock, Check, X, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { StatusActions } from "./StatusActions";
import { StorageImage } from "@/components/StorageImage";

export interface PackageTableItem {
  id: string;
  thumbnailUrl?: string;
  qrCodeUrl?: string;
  user: {
    id: string;
    fullName: string;
    email: string;
    phone?: string;
    address?: string;
    pickupDate?: string;
    pickupTime?: string;
    pickupStatus?: "not-scheduled" | "pending" | "approved";
    pickupId?: string;
    assignedOwner?: {
      id: string;
      name: string;
    };
  };
  manager?: {
    id: string;
    name: string;
  };
  proposedPickup?: {
    date: string;
    timeWindow: string;
    rawDate?: string; // ISO date string for date comparison
  };
  approvalStatus: "pending" | "approved" | "rejected";
  status: "pending-review" | "awaiting-confirmation" | "pickup-scheduled" | "confirmed" | "picked-up" | "delivered" | "returned" | "archived" | "user-canceled";
  packageStatus: string; // raw DB status for display
  canceledAt?: string;
  cancelReason?: string;
}

interface ManagerOption {
  id: string;
  name: string;
}

interface PackagesTableProps {
  packages: PackageTableItem[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  onRowClick?: (pkg: PackageTableItem) => void;
  onNotifyAgain?: (packageId: string) => void;
  onEditPackage?: (packageId: string) => void;
  onApprovePackage?: (packageId: string) => void;
  onDenyPackage?: (packageId: string) => void;
  onApprovePickup?: (userId: string) => void;
  onDenyPickup?: (userId: string) => void;
  onMarkPickedUp?: (packageId: string) => void;
  onUploadConfirmation?: (packageId: string) => void;
  onSchedulePickupForUser?: (userId: string) => void;
  onArchivePackage?: (packageId: string) => void;
  onReschedule?: (packageId: string) => void;
  onAssignUserManager?: (userId: string, managerId: string | null) => void;
  managers?: ManagerOption[];
  showStatusActions?: boolean;
  emptyMessage?: string;
}

const statusConfig = {
  "pending-review": {
    label: "Pending Review",
    className: "bg-amber-100 text-amber-800 border-amber-200",
  },
  "awaiting-confirmation": {
    label: "Awaiting Confirmation",
    className: "bg-orange-100 text-orange-800 border-orange-200",
  },
  "pickup-scheduled": {
    label: "Pickup Scheduled",
    className: "bg-purple-100 text-purple-800 border-purple-200",
  },
  confirmed: {
    label: "Confirmed",
    className: "bg-emerald-100 text-emerald-800 border-emerald-200",
  },
  "picked-up": {
    label: "Picked Up",
    className: "bg-blue-100 text-blue-800 border-blue-200",
  },
  delivered: {
    label: "Delivered",
    className: "bg-green-100 text-green-800 border-green-200",
  },
  returned: {
    label: "Returned",
    className: "bg-teal-100 text-teal-800 border-teal-200",
  },
  archived: {
    label: "Archived",
    className: "bg-gray-100 text-gray-600 border-gray-200",
  },
  "user-canceled": {
    label: "Canceled by User",
    className: "bg-red-100 text-red-800 border-red-200",
  },
};

interface GroupedPackages {
  user: PackageTableItem["user"];
  packages: PackageTableItem[];
}

export const PackagesTable = ({
  packages,
  selectedIds,
  onSelectionChange,
  onRowClick,
  onNotifyAgain,
  onEditPackage,
  onApprovePackage,
  onDenyPackage,
  onApprovePickup,
  onDenyPickup,
  onMarkPickedUp,
  onUploadConfirmation,
  onSchedulePickupForUser,
  onArchivePackage,
  onReschedule,
  onAssignUserManager,
  managers = [],
  showStatusActions = false,
  emptyMessage = "No packages found",
}: PackagesTableProps) => {
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  
  // Group packages by user
  const groupedPackages = useMemo(() => {
    const groups: Record<string, GroupedPackages> = {};
    packages.forEach((pkg) => {
      if (!groups[pkg.user.id]) {
        groups[pkg.user.id] = {
          user: pkg.user,
          packages: [],
        };
      }
      groups[pkg.user.id].packages.push(pkg);
    });
    return Object.values(groups);
  }, [packages]);

  const allSelected = packages.length > 0 && selectedIds.length === packages.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < packages.length;

  const toggleAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(packages.map((p) => p.id));
    }
  };

  const toggleOne = (id: string) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((i) => i !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const toggleUserExpand = (userId: string) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleUserPackages = (userPackages: PackageTableItem[]) => {
    const userPackageIds = userPackages.map((p) => p.id);
    const allUserPackagesSelected = userPackageIds.every((id) => selectedIds.includes(id));
    
    if (allUserPackagesSelected) {
      onSelectionChange(selectedIds.filter((id) => !userPackageIds.includes(id)));
    } else {
      const newSelection = new Set([...selectedIds, ...userPackageIds]);
      onSelectionChange(Array.from(newSelection));
    }
  };

  const isUserFullySelected = (userPackages: PackageTableItem[]) => {
    return userPackages.every((p) => selectedIds.includes(p.id));
  };

  const isUserPartiallySelected = (userPackages: PackageTableItem[]) => {
    const selected = userPackages.filter((p) => selectedIds.includes(p.id));
    return selected.length > 0 && selected.length < userPackages.length;
  };

  if (packages.length === 0) {
    return (
      <div className="rounded-lg border bg-card overflow-hidden shadow-sm">
        <div className="h-24 flex items-center justify-center text-muted-foreground">
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card overflow-hidden shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-muted/50 border-b">
        <Checkbox
          checked={allSelected}
          ref={(el) => {
            if (el) (el as any).indeterminate = someSelected;
          }}
          onCheckedChange={toggleAll}
          aria-label="Select all"
        />
        <span className="text-sm font-medium text-muted-foreground">
          {groupedPackages.length} users · {packages.length} packages
        </span>
      </div>

      {/* Grouped content */}
      <div className="divide-y">
        {groupedPackages.map((group) => {
          const isExpanded = expandedUsers.has(group.user.id);
          const userFullySelected = isUserFullySelected(group.packages);
          const userPartiallySelected = isUserPartiallySelected(group.packages);

          return (
            <Collapsible
              key={group.user.id}
              open={isExpanded}
              onOpenChange={() => toggleUserExpand(group.user.id)}
            >
              {/* User header row */}
              <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                <Checkbox
                  checked={userFullySelected}
                  ref={(el) => {
                    if (el) (el as any).indeterminate = userPartiallySelected;
                  }}
                  onCheckedChange={() => toggleUserPackages(group.packages)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Select all packages for ${group.user.fullName}`}
                />
                
                <CollapsibleTrigger asChild>
                  <button className="flex flex-col gap-2 flex-1 text-left">
                    {/* Row 1: User identity + expand arrow */}
                    <div className="flex items-center gap-3 w-full">
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      
                      <div className="flex items-center gap-2 min-w-[180px]">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{group.user.fullName}</p>
                          <p className="text-xs text-muted-foreground">{group.user.email}</p>
                          {group.user.phone && (
                            <p className="text-xs text-muted-foreground">{group.user.phone}</p>
                          )}
                        </div>
                      </div>

                      {/* User Address */}
                      {group.user.address && (() => {
                        const commaIdx = group.user.address.indexOf(",");
                        const line1 = commaIdx > -1 ? group.user.address.substring(0, commaIdx) : group.user.address;
                        const line2 = commaIdx > -1 ? group.user.address.substring(commaIdx + 1).trim() : "";
                        return (
                          <div className="flex items-start gap-1 text-xs text-muted-foreground min-w-[160px]">
                            <MapPin className="h-3 w-3 shrink-0 mt-0.5" />
                            <div className="leading-tight">
                              <span className="block">{line1}</span>
                              {line2 && <span className="block">{line2}</span>}
                            </div>
                          </div>
                        );
                      })()}

                      <div className="ml-auto flex items-center gap-2">
                        <Badge variant="secondary" className="shrink-0">
                          <Package className="h-3 w-3 mr-1" />
                          {group.packages.length} package{group.packages.length !== 1 ? "s" : ""}
                        </Badge>
                      </div>
                    </div>

                    {/* Row 2: Pickup details + assignment + status */}
                    <div className="flex items-center gap-3 pl-8 flex-wrap">
                      {/* Pickup Date & Time */}
                      {group.user.pickupDate ? (
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="flex items-center gap-1 text-xs">
                            <CalendarDays className="h-3 w-3 text-muted-foreground" />
                            <span className="font-medium">{group.user.pickupDate}</span>
                          </div>
                          {group.user.pickupTime && (
                            <div className="flex items-center gap-1 text-xs">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              <span>{group.user.pickupTime}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">No pickup scheduled</span>
                      )}

                      {/* Pickup Status - non-pending */}
                      {group.user.pickupStatus && group.user.pickupStatus !== "pending" && (
                        <Badge
                          variant="outline"
                          className={cn("shrink-0 text-xs",
                            group.user.pickupStatus === "approved" && "bg-emerald-100 text-emerald-800 border-emerald-200",
                            group.user.pickupStatus === "not-scheduled" && "bg-muted text-muted-foreground",
                          )}
                        >
                          {group.user.pickupStatus === "approved" ? "Approved" : "Not Scheduled"}
                        </Badge>
                      )}

                      {/* Assignment Dropdown */}
                      <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={group.packages[0]?.user.assignedOwner?.id || "unassigned"}
                          onValueChange={(value) => {
                            onAssignUserManager?.(group.user.id, value === "unassigned" ? null : value);
                          }}
                        >
                          <SelectTrigger className={cn(
                            "h-7 text-xs border rounded-full px-2.5 gap-1 font-medium",
                            group.packages[0]?.user.assignedOwner
                              ? "bg-primary/5 border-primary/20 text-primary"
                              : "bg-muted text-muted-foreground border-border"
                          )}>
                            <UserPlus className="h-3 w-3 shrink-0" />
                            <span>
                              {group.packages[0]?.user.assignedOwner
                                ? group.packages[0].user.assignedOwner.name
                                : "Unassigned"}
                            </span>
                          </SelectTrigger>
                          <SelectContent className="bg-popover">
                            <SelectItem value="unassigned">Unassigned</SelectItem>
                            {managers.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                {m.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </button>
                </CollapsibleTrigger>
                
                {/* Schedule Pickup for User button - only show if no approved pickup */}
                {onSchedulePickupForUser && group.user.pickupStatus !== "approved" && group.packages.some(p => p.status === "awaiting-confirmation") && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-2 shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSchedulePickupForUser(group.user.id);
                    }}
                  >
                    <CalendarPlus className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Schedule</span>
                  </Button>
                )}

                {/* User-level Pickup Approve/Deny - inline orange button for pending */}
                {group.user.pickupStatus === "pending" && group.user.pickupId && (
                  <div className="flex items-center gap-1 ml-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          className="bg-orange-500 hover:bg-orange-600 text-white h-7 text-xs font-medium gap-1"
                        >
                          <CalendarClock className="h-3 w-3" />
                          Pending Approval
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-popover">
                        <DropdownMenuItem
                          className="text-emerald-700 focus:text-emerald-800 focus:bg-emerald-50 cursor-pointer"
                          onClick={() => onApprovePickup?.(group.user.id)}
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Approve Pickup
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive focus:bg-red-50 cursor-pointer"
                          onClick={() => onDenyPickup?.(group.user.id)}
                        >
                          <X className="h-4 w-4 mr-2" />
                          Deny / Suggest Alternate
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>

              {/* Packages table */}
              <CollapsibleContent>
                <div className="bg-muted/20 border-t">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableHead className="w-[100px] pl-12">Package</TableHead>
                        <TableHead>Approval</TableHead>
                        <TableHead>Package Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.packages.map((pkg) => (
                        <TableRow
                          key={pkg.id}
                          className={cn(
                            "hover:bg-muted/40 cursor-pointer transition-colors",
                            selectedIds.includes(pkg.id) && "bg-primary/5"
                          )}
                          onClick={() => onRowClick?.(pkg)}
                        >
                          {/* Package Thumbnail */}
                          <TableCell className="pl-12">
                            <div className="relative w-12 h-12 rounded-lg overflow-hidden bg-muted border">
                              {pkg.thumbnailUrl ? (
                                <StorageImage
                                  bucket="package-photos"
                                  storedValue={pkg.thumbnailUrl}
                                  alt="Package"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                  <QrCode className="h-4 w-4" />
                                </div>
                              )}
                              <div className="absolute bottom-0.5 right-0.5 p-0.5 bg-background/90 rounded shadow-sm">
                                <QrCode className="h-2 w-2 text-foreground" />
                              </div>
                            </div>
                          </TableCell>

                          {/* Approval Status */}
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={cn("text-xs font-medium",
                                pkg.approvalStatus === "pending" && "bg-amber-100 text-amber-800 border-amber-200",
                                pkg.approvalStatus === "approved" && "bg-emerald-100 text-emerald-800 border-emerald-200",
                                pkg.approvalStatus === "rejected" && "bg-red-100 text-red-800 border-red-200",
                              )}
                            >
                              {pkg.approvalStatus === "pending" ? "Pending" : pkg.approvalStatus === "approved" ? "Approved" : "Rejected"}
                            </Badge>
                          </TableCell>

                          {/* Package Status */}
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={cn("text-xs font-medium", statusConfig[pkg.status].className)}
                            >
                              {statusConfig[pkg.status].label}
                            </Badge>
                          </TableCell>


                          {/* Actions */}
                          <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-2">
                              {/* Package-level Approve/Deny */}
                              {pkg.approvalStatus === "pending" && (
                                <>
                                  <Button
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs"
                                    onClick={() => onApprovePackage?.(pkg.id)}
                                  >
                                    <Check className="h-3 w-3 mr-1" />
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="h-7 text-xs"
                                    onClick={() => onDenyPackage?.(pkg.id)}
                                  >
                                    <X className="h-3 w-3 mr-1" />
                                    Deny
                                  </Button>
                                </>
                              )}

                              {/* Status-specific actions for non-pending packages */}
                              {pkg.approvalStatus !== "pending" && showStatusActions && (
                                <StatusActions
                                  status={pkg.status}
                                  pickupDate={pkg.proposedPickup?.rawDate}
                                  onMarkPickedUp={() => onMarkPickedUp?.(pkg.id)}
                                  onUploadConfirmation={() => onUploadConfirmation?.(pkg.id)}
                                  onReschedule={() => onReschedule?.(pkg.id)}
                                />
                              )}

                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-7 w-7">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-popover">
                                  <DropdownMenuItem onClick={() => onRowClick?.(pkg)}>
                                    View Details
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
};
